// src/services/authService.ts
import { User as FirebaseUser } from 'firebase/auth';

interface User extends Omit<FirebaseUser, 'phoneNumber'> {
  id?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  teacherId?: string;
  grade?: string;
  school?: string;
  schoolId?: string;
  bio?: string;
  expertise?: string;
  onboardingCompleted?: boolean;
  strictModeActivated?: boolean;
  phoneNumber: string | null;
}

interface LoginResponse {
  user: User;
  token: string;
  refreshToken?: string;
}

interface RegisterResponse {
  user: User;
  token: string;
}

// Define your registration data shape.
export interface RegistrationData {
  firstName: string;
  lastName: string;
  email?: string;
  password: string;
  phoneNumber?: string;
  role?: string;
  grade?: string;
  school?: string;
  schoolId?: string;
  teacherId?: string;
  bio?: string;
  expertise?: string;
  biometrics?: {
    faceTemplate?: string;
    voiceSignature?: string;
    rawData?: string;
  };
}

const API_URL = process.env.REACT_APP_API_URL || "https://lavernaibackend.onrender.com/api";

const authService = {
  login: async (emailOrPhone: string, password: string, captchaToken: string, role?: string, isPhoneNumber: boolean = false): Promise<LoginResponse> => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...(isPhoneNumber ? { phoneNumber: emailOrPhone } : { email: emailOrPhone }),
          password,
          captchaToken,
          role
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        // Handle specific error messages
        if (data.message?.includes('already exists')) {
          throw new Error('An account with this email already exists. Please try logging in instead.');
        }
        if (data.message?.includes('Invalid credentials')) {
          throw new Error('Invalid credentials. Please try again.');
        }
        if (data.message?.includes('Account is temporarily locked')) {
          throw new Error('Your account has been temporarily locked due to too many failed attempts. Please try again later.');
        }
        throw new Error(data.message || "Login failed");
      }

      if (data.status !== 'success') {
        throw new Error(data.message || "Login failed");
      }

      const userRaw = data.data.user;
      const normalizedUser: User = {
        ...userRaw,
        id: userRaw.id || userRaw.uid,
        email: userRaw.email || '',
        name: `${userRaw.firstName || ''} ${userRaw.lastName || ''}`.trim(),
        firstName: userRaw.firstName || '',
        lastName: userRaw.lastName || '',
        role: userRaw.role || 'student',
        grade: userRaw.grade || '',
        school: userRaw.school || '',
        schoolId: userRaw.schoolId || '',
        teacherId: userRaw.teacherId || '',
        phoneNumber: userRaw.phoneNumber || '',
        onboardingCompleted: userRaw.onboardingCompleted || false,
        strictModeActivated: false
      };

      return { 
        user: normalizedUser, 
        token: data.token,
        refreshToken: data.refreshToken
      };
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || "Login failed");
    }
  },

  register: async (registrationData: RegistrationData): Promise<RegisterResponse> => {
    try {
      console.log('Attempting registration with:', registrationData);
      
      // Validate required fields based on role
      if (registrationData.role === 'competitor' && !registrationData.phoneNumber) {
        throw new Error('Phone number is required for competitor registration');
      }
      
      if (registrationData.role !== 'competitor' && !registrationData.email) {
        throw new Error('Email is required for registration');
      }

      const response = await fetch(`${API_URL}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstName: registrationData.firstName,
          lastName: registrationData.lastName,
          ...(registrationData.email && { email: registrationData.email }),
          password: registrationData.password,
          ...(registrationData.phoneNumber && { phoneNumber: registrationData.phoneNumber }),
          role: registrationData.role || 'student',
          ...(registrationData.grade && { grade: registrationData.grade }),
          ...(registrationData.school && { school: registrationData.school }),
          ...(registrationData.schoolId && { schoolId: registrationData.schoolId }),
          ...(registrationData.teacherId && { teacherId: registrationData.teacherId }),
          ...(registrationData.bio && { bio: registrationData.bio }),
          ...(registrationData.expertise && { expertise: registrationData.expertise })
        }),
      });

      const data = await response.json();
      console.log('Registration response:', { status: response.status, data });
      
      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }

      if (data.status !== 'success') {
        throw new Error(data.message || "Registration failed");
      }

      const userRaw = data.data.user;
      const normalizedUser: User = {
        ...userRaw,
        id: userRaw.id || userRaw.uid,
        email: userRaw.email || '',
        displayName: `${userRaw.firstName} ${userRaw.lastName}`,
        role: userRaw.role || 'student',
        grade: userRaw.grade || '',
        school: userRaw.school || '',
        schoolId: userRaw.schoolId || '',
        teacherId: userRaw.teacherId || '',
        phoneNumber: userRaw.phoneNumber || '',
        onboardingCompleted: userRaw.onboardingCompleted || false,
        strictModeActivated: false
      };

      return { user: normalizedUser, token: data.token };
    } catch (error: any) {
      console.error('Registration error details:', {
        message: error.message,
        response: error.response,
        data: error.data
      });
      throw new Error(error.message || "Registration failed");
    }
  },

  refreshToken: async (refreshToken: string): Promise<{ token: string; refreshToken: string }> => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || "https://lavernaibackend.onrender.com/api";
      const response = await fetch(`${API_URL}/auth/refresh-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || "Token refresh failed");
      }

      if (data.status !== 'success') {
        throw new Error(data.message || "Token refresh failed");
      }

      return { 
        token: data.token,
        refreshToken: data.refreshToken 
      };
    } catch (error: any) {
      console.error('Token refresh error:', error);
      throw new Error(error.message || "Token refresh failed");
    }
  },
};

export default authService;
